// ============================================================
// REACT HOOKS for displaying photos from the Supabase Storage bucket.
// Wraps the photoStorage utility so display sites can just say:
//
//   const url = usePhotoUrl(supabase, voter.photo_url);
//   <img src={url || undefined} />
//
// Both hooks return null while loading and on failure, so display
// sites can use a `url || initials` fallback pattern.
// ============================================================

import { useState, useEffect } from 'react';
import { getDisplayUrl, getDisplayUrls } from './photoStorage';

// Resolve a single photo_url value. Re-runs when the value changes.
export function usePhotoUrl(supabase, photoUrlField) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!photoUrlField) {
      setUrl(null);
      return;
    }
    getDisplayUrl(supabase, photoUrlField).then((resolved) => {
      if (!cancelled) setUrl(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [supabase, photoUrlField]);

  return url;
}

// Resolve many photo_url values at once. Returns a Map from raw value
// → resolved URL. Use this in components that render lots of avatars
// (FamilyTree, household lists, etc.) to avoid one round trip per photo.
export function usePhotoUrls(supabase, photoUrlFields) {
  const [urls, setUrls] = useState(() => new Map());

  // Stable key for the dependency array: join the values. This re-fetches
  // when the SET of photo_urls changes, but not on unrelated re-renders.
  const key = (photoUrlFields || []).filter(Boolean).sort().join('|');

  useEffect(() => {
    let cancelled = false;
    const values = (photoUrlFields || []).filter(Boolean);
    if (values.length === 0) {
      setUrls(new Map());
      return;
    }
    getDisplayUrls(supabase, values).then((map) => {
      if (!cancelled) setUrls(map);
    });
    return () => {
      cancelled = true;
    };
  }, [supabase, key]);

  return urls;
}
